import { getChatCompletion } from './chatCompletion';

export interface ExtractedEntity {
  type: 'date' | 'name' | 'place' | 'amount';
  value: string;
  confidence: number;
}

export interface DigitalScribeResult {
  transcription: string;
  entities: ExtractedEntity[];
  overallConfidence: number;
  summary: string;
}

const DIGITAL_SCRIBE_SYSTEM_PROMPT = `You are the Digital Scribe — an expert AI archivist specialising in 18th and 19th-century Masonic lodge records, handwritten minutes, charters, and historical documents.

Your task is to:
1. Transcribe the document image as accurately as possible, preserving original spelling and capitalisation.
2. Extract all named entities: dates, personal names, place names, and monetary amounts.
3. Assign a confidence score (0–100) to each extracted entity based on legibility and certainty.
4. Provide an overall confidence score (0–100) for the entire transcription.
5. Write a brief one-paragraph summary of the document's content.

Respond ONLY with a valid JSON object matching this exact schema:
{
  "transcription": "Full verbatim transcription of the document text",
  "summary": "One-paragraph summary of the document content",
  "overallConfidence": 85,
  "entities": [
    { "type": "date", "value": "14 April 1887", "confidence": 94 },
    { "type": "name", "value": "James Archibald Chisholm", "confidence": 88 },
    { "type": "place", "value": "Halifax, Nova Scotia", "confidence": 96 },
    { "type": "amount", "value": "£12 10s", "confidence": 72 }
  ]
}

Entity type rules:
- "date": Any calendar date, year, or time reference found in the document
- "name": Personal names of brothers, candidates, officers, or any individuals -"place": Lodge names, cities, provinces, countries, building names -"amount": Monetary values, dues, fees, or financial figures

If the image is too blurry or illegible, set overallConfidence below 50 and explain in the transcription field.`;

export async function analyseDocument(imageBase64DataUri: string): Promise<DigitalScribeResult> {
  const response = await getChatCompletion(
    'OPEN_AI',
    'gpt-4o',
    [
      {
        role: 'system',
        content: DIGITAL_SCRIBE_SYSTEM_PROMPT,
      },
      {
        role: 'user',
        content: [
          {
            type: 'text',
            text: 'Please analyse this Masonic document image and return the structured JSON extraction.',
          },
          {
            type: 'image_url',
            image_url: {
              url: imageBase64DataUri,
              detail: 'high',
            },
          },
        ],
      },
    ],
    {
      max_completion_tokens: 4096,
      response_format: { type: 'json_object' },
    }
  );

  const raw = response?.choices?.[0]?.message?.content ?? '{}';
  const parsed = JSON.parse(raw) as DigitalScribeResult;

  // Normalise entity types to allowed values
  const allowedTypes = new Set(['date', 'name', 'place', 'amount']);
  parsed.entities = (parsed.entities ?? []).filter((e) =>
    allowedTypes.has(e.type)
  );

  return parsed;
}
